<template>
    <div class="notice-detail-container">
        <div class="jc-page-top">
            <div class="instance-list">
                <div class="jc-page-header">
                    <h1>
                        <i class="jc-icon-arrow-left left-icon" @click="goBack"></i>{{ $t(config.listTitleKey) }}
                    </h1>
                </div>
                <div class="jc-page-content" v-loading="loading">
                    <div class="notice-detail">
                        <div class="notice-card">
                            <h2 class="notice-title">{{ info.title }}</h2>
                            <div class="notice-meta">{{ info.createTime }}</div>
                            <div class="divider"></div>
                            <div class="notice-body">
                                <pre v-html="formattedContent"></pre>
                            </div>
                            <div class="notice-signature">
                                <div>{{ $t('overview.notice.jdTeam') }}</div>
                                <div v-if="info.publishTime" class="signature-date">{{ info.publishTime }}</div>
                            </div>
                        </div>

                        <div class="notice-footer-nav">
                            <div class="nav-left">
                                <div class="nav-item">
                                    {{ $t('overview.notice.prevNotice') }}：<span
                                        class="link-text"
                                        :class="!info.prev ? 'disabledText' : ''"
                                        @click="goToNotice('prev')"
                                    >{{ info.prev ? info.prev.title : $t('overview.notice.noPre') }}</span>
                                </div>
                                <div class="nav-item">
                                    {{ $t('overview.notice.nextNotice') }}：<span
                                        class="link-text"
                                        :class="!info.next ? 'disabledText' : ''"
                                        @click="goToNotice('next')"
                                    >{{ info.next ? info.next.title : $t('overview.notice.noNext') }}</span>
                                </div>
                            </div>
                            <div class="nav-right back-link" @click="goList">
                                <i class="jc-icon-arrow-left"></i>{{ $t(config.backListKey) }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { getNoticeConfig } from './noticeConfig';

/**
 * 公告详情页（UI 展示层）
 *
 * 架构说明：
 *   - 通过路由 query.noticeType 确定当前公告类型，默认为 'announcement'
 *   - 由 noticeConfig.js（数据处理层）提供接口方法、字段适配（normalizeDetail）
 *   - UI 层只消费标准化的 NoticeDetail 格式：title / content / createTime / prev / next
 *   - 上下篇导航、返回列表均使用配置中的路由信息，无硬编码路径
 */
export default {
    name: 'NoticeDetail',
    data() {
        return {
            rawInfo: {},
            loading: false,
        };
    },
    computed: {
        /**
         * 从路由 query 中读取通知类型，获取对应配置（数据处理层）
         */
        config() {
            return getNoticeConfig(this.$route.query.noticeType);
        },
        /**
         * 经数据处理层适配后的标准化详情对象
         * 字段：title / content / createTime / publishTime / prev / next
         */
        info() {
            if (!this.rawInfo || !Object.keys(this.rawInfo).length) {
                return {};
            }
            return this.config.normalizeDetail(this.rawInfo);
        },
        /**
         * 格式化公告内容（保留换行、替换制表符为空格）
         */
        formattedContent() {
            if (!this.info.content) return '';
            return this.info.content
                .replace(/\n/g, '<br>')
                .replace(/\\t/g, '&nbsp;');
        },
    },
    watch: {
        /**
         * 监听路由 instId 变化（上下篇导航时路由会变更）
         * $router.push 是异步的，$route 更新后才能拿到新 instId，因此不能用 $nextTick
         */
        '$route.query.instId'(newVal, oldVal) {
            if (newVal && newVal !== oldVal) {
                this.fetchDetail();
            }
        },
    },
    mounted() {
        this.fetchDetail();
    },
    methods: {
        /**
         * 获取详情数据
         * 通过配置的 detailApiMethod 动态调用接口
         */
        fetchDetail() {
            const apiFn = this.$api[this.config.detailApiMethod];
            if (typeof apiFn !== 'function') {
                console.error('[NoticeDetail] 接口方法不存在: ' + this.config.detailApiMethod);
                return;
            }
            this.loading = true;
            const params = { instId: this.$route.query.instId };
            apiFn(params)
                .then(res => {
                    if (res.errno === 10000 && res.data) {
                        this.rawInfo = this.config.extractDetailData(res.data);
                    }
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        /**
         * 返回上一页
         */
        goBack() {
            this.$router.go(-1);
        },
        /**
         * 返回列表页，携带 noticeType
         */
        goList() {
            this.$router.replace({
                path: this.config.listRoute,
                query: { noticeType: this.config.type },
            });
        },
        /**
         * 跳转上一篇或下一篇
         * @param {'prev'|'next'} direction
         */
        goToNotice(direction) {
            const target = this.info[direction];
            if (!target) return;
            // push 后由 watch '$route.query.instId' 监听变化并自动重新拉取详情
            this.$router.push({
                path: this.config.detailRoute,
                query: {
                    instId: target.instId,
                    noticeType: this.config.type,
                },
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.notice-detail-container {
    background-color: #f6fafd;
    height: 100%;
    overflow-y: auto;
}

// header spacing adjustments
.jc-page-header h1 {
    display: flex;
    align-items: center;
    font-size: 18px;
    color: rgba(0, 0, 0, 0.85);
}

.left-icon {
    cursor: pointer;
    margin-right: 10px;
}

.notice-detail {
    padding: 0 24px 24px;
}

.notice-card {
    background: #fff;
    border: 1px solid #d9d9d9;
    padding: 24px 32px;
    margin-bottom: 20px;
    min-height: 400px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
}

.notice-title {
    text-align: center;
    font-size: 24px;
    font-weight: bold;
    margin: 0 0 10px 0;
    color: rgba(0, 0, 0, 0.85);
}

.notice-meta {
    text-align: center;
    color: rgba(0, 0, 0, 0.45);
    font-size: 14px;
    margin: 20px 0;
}

.divider {
    height: 1px;
    background: #eee;
    margin: 20px 0;
}

.notice-body {
    min-height: 200px;

    pre {
        font-family: inherit;
        white-space: pre-wrap;
        word-wrap: break-word;
        line-height: 2;
        font-size: 14px;
        color: #333;
        margin: 0;
        text-align: justify;
    }
}

.notice-signature {
    text-align: right;
    color: #333;
    font-size: 14px;
    line-height: 1.8;
    font-weight: 500;
}

.notice-footer-nav {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 14px;
    color: #666;
    margin-top: 20px;
}

.nav-left {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.link-text {
    color: #666;
    cursor: pointer;
    transition: color 0.3s;

    &:hover {
        color: #3c6ef0;
    }
}

.disabledText {
    color: rgba(0, 0, 0, .25) !important;
    cursor: not-allowed !important;
}

.back-link {
    color: #3c6ef0;
    cursor: pointer;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
}
.jc-page-header {
  height: 45px !important;
  line-height: 45px !important;
}
</style>



// WEBPACK FOOTER //
// src/components/notice/Detail.vue